import { useCallback, useEffect, useRef } from 'react';

import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_PACK_ARCH_TABLE_CONFIGURATION_CHANGED,
    PPA_PACK_ARCH_TABLE_SEARCH as PPA_TRACK_TABLE_SEARCH,
    PPA_PACK_ARCH_TABLE_SEARCH_EMPTY,
    PPA_PACK_ARCH_TABLE_SECTION_VIEWED,
} from 'utils/mixpanelEventNames';

type UsePackArchitectureTableTrackingParams = {
    showTable: boolean;
    tableSectionRef: { current: HTMLDivElement | null };
    search: string;
    submitSearch: (override?: string) => void;
    selectedKpis: string[];
    toggleKpi: (kpiId: string) => void;
    isFiltered: boolean;
    showTableLoader: boolean;
    isAppendingPages: boolean;
    isChartTableRefetching: boolean;
    isTableEmpty: boolean;
    filteredRowCount: number;
};

export function usePackArchitectureTableTracking({
    showTable,
    tableSectionRef,
    search,
    submitSearch,
    selectedKpis,
    toggleKpi,
    isFiltered,
    showTableLoader,
    isAppendingPages,
    isChartTableRefetching,
    isTableEmpty,
    filteredRowCount,
}: UsePackArchitectureTableTrackingParams) {
    const hasTrackedSectionViewRef = useRef(false);
    const prevSearchTermRef = useRef('');
    const lastEmptySearchTermRef = useRef('');

    useEffect(() => {
        if (!showTable || hasTrackedSectionViewRef.current) return;
        const node = tableSectionRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !hasTrackedSectionViewRef.current) {
                    hasTrackedSectionViewRef.current = true;
                    trackEventMixpanel(PPA_PACK_ARCH_TABLE_SECTION_VIEWED, {
                        time_from_page_load_sec: Math.round(performance.now() / 1000),
                    });
                    observer.disconnect();
                }
            },
            { threshold: 0.1 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [showTable, tableSectionRef]);

    useEffect(() => {
        if (!isFiltered || showTableLoader || isAppendingPages || isChartTableRefetching) return;

        const isSearchEmpty = isTableEmpty || filteredRowCount === 0;
        if (!isSearchEmpty) return;

        const failedTerm = prevSearchTermRef.current.trim();
        if (!failedTerm || failedTerm === lastEmptySearchTermRef.current) return;

        lastEmptySearchTermRef.current = failedTerm;
        trackEventMixpanel(PPA_PACK_ARCH_TABLE_SEARCH_EMPTY, {
            search_term_failed: failedTerm,
        });
    }, [
        filteredRowCount,
        isAppendingPages,
        isChartTableRefetching,
        isFiltered,
        isTableEmpty,
        showTableLoader,
    ]);

    const handleSearchSubmit = useCallback(
        (override?: string) => {
            const term = override !== undefined ? override : search;
            prevSearchTermRef.current = term ?? '';
            lastEmptySearchTermRef.current = '';
            submitSearch(override);
            if (term) {
                trackEventMixpanel(PPA_TRACK_TABLE_SEARCH, { search_term: term });
            }
        },
        [search, submitSearch],
    );

    const handleToggleKpi = useCallback(
        (kpiId: string) => {
            const nextSelected = selectedKpis.includes(kpiId)
                ? selectedKpis.filter((id) => id !== kpiId)
                : [...selectedKpis, kpiId];
            trackEventMixpanel(PPA_PACK_ARCH_TABLE_CONFIGURATION_CHANGED, {
                visible_kpis: nextSelected,
                kpi_count: nextSelected.length,
            });
            toggleKpi(kpiId);
        },
        [selectedKpis, toggleKpi],
    );

    return { handleSearchSubmit, handleToggleKpi };
}
